import { CoreEnums } from '@/utils/domain/constants';
import _ from 'lodash';

const { ActionType } = CoreEnums;

/**
 * Generates action configuration list from ActionType enum
 * @param {Function} formatMessage - Intl formatMessage function
 * @returns {Array} Array of action configurations with id, type, title, description
 */
export function generateActionConfigFromEnum(formatMessage) {
  // Map ActionType enum to action configurations
  const actionConfigs = [];

  // Iterate through all ActionType values
  Object.keys(ActionType).forEach((key) => {
    const type = ActionType[key];

    // Generate unique ID for each action type
    const id = `action_${type}`;

    // Get title from i18n using the applications.text.actionType pattern
    const title = formatMessage({
      id: `applications.text.actionType${type}`,
      defaultMessage: key, // Fallback to enum key if translation missing
    });

    // Generate description based on action type
    let description = '';
    switch (type) {
      case ActionType.DefaultInitial:
        description = formatMessage({
          id: 'actions.description.defaultInitial',
          defaultMessage: 'Acción inicial del proceso',
        });
        break;
      case ActionType.CompleteInformation:
        description = formatMessage({
          id: 'actions.description.completeInformation',
          defaultMessage: 'Permite completar información mediante formularios',
        });
        break;
      case ActionType.Approval:
        description = formatMessage({
          id: 'actions.description.approval',
          defaultMessage: 'Requiere aprobación manual de un usuario',
        });
        break;
      case ActionType.AutoApprove:
        description = formatMessage({
          id: 'actions.description.autoApprove',
          defaultMessage: 'Aprueba automáticamente sin intervención manual',
        });
        break;
      case ActionType.Conditional:
        description = formatMessage({
          id: 'actions.description.conditional',
          defaultMessage: 'Evalúa condiciones para determinar el flujo',
        });
        break;
      case ActionType.ExecuteTask:
        description = formatMessage({
          id: 'actions.description.executeTask',
          defaultMessage: 'Ejecuta una tarea específica del sistema',
        });
        break;
      case ActionType.ExecuteApiCall:
        description = formatMessage({
          id: 'actions.description.executeApiCall',
          defaultMessage: 'Realiza llamadas a APIs externas',
        });
        break;
      case ActionType.AutoReject:
        description = formatMessage({
          id: 'actions.description.autoReject',
          defaultMessage: 'Rechaza automáticamente sin intervención manual',
        });
        break;
      case ActionType.DigitalSignature:
        description = formatMessage({
          id: 'actions.description.digitalSignature',
          defaultMessage: 'Requiere firma digital del usuario',
        });
        break;
      case ActionType.SendEmail:
        description = formatMessage({
          id: 'actions.description.sendEmail',
          defaultMessage: 'Envía correos electrónicos automáticamente',
        });
        break;
      case ActionType.ExecuteQuery:
        description = formatMessage({
          id: 'actions.description.executeQuery',
          defaultMessage: 'Ejecuta consultas en base de datos',
        });
        break;
      case ActionType.DynamicEntityOperation:
        description = formatMessage({
          id: 'actions.description.dynamicEntityOperation',
          defaultMessage: 'Realiza operaciones sobre entidades dinámicas',
        });
        break;
      case ActionType.GenerateExcel:
        description = formatMessage({
          id: 'actions.description.generateExcel',
          defaultMessage: 'Genera archivos Excel con datos',
        });
        break;
      case ActionType.StartProcessInstance:
        description = formatMessage({
          id: 'actions.description.startProcessInstance',
          defaultMessage: 'Inicia una nueva instancia de proceso',
        });
        break;
      case ActionType.SetVariableData:
        description = formatMessage({
          id: 'actions.description.setVariableData',
          defaultMessage: 'Establece valores de variables del proceso',
        });
        break;
      case ActionType.SetFormData:
        description = formatMessage({
          id: 'actions.description.setFormData',
          defaultMessage: 'Establece datos en formularios',
        });
        break;
      case ActionType.Parallel:
        description = formatMessage({
          id: 'actions.description.parallel',
          defaultMessage: 'Ejecuta múltiples acciones en paralelo',
        });
        break;
      case ActionType.GenerateDocuments:
        description = formatMessage({
          id: 'actions.description.generateDocuments',
          defaultMessage: 'Genera documentos basados en plantillas',
        });
        break;
      case ActionType.ScheduledExecution:
        description = formatMessage({
          id: 'actions.description.scheduledExecution',
          defaultMessage: 'Programa la ejecución para un momento específico',
        });
        break;
      case ActionType.SharePointOperation:
        description = formatMessage({
          id: 'actions.description.sharePointOperation',
          defaultMessage: 'Realiza operaciones en SharePoint',
        });
        break;
      case ActionType.Iterate:
        description = formatMessage({
          id: 'actions.description.iterate',
          defaultMessage: 'Itera sobre colecciones de datos',
        });
        break;
      case ActionType.Switch:
        description = formatMessage({
          id: 'actions.description.switch',
          defaultMessage: 'Evalúa múltiples condiciones como switch',
        });
        break;
      case ActionType.InternalOperation:
        description = formatMessage({
          id: 'actions.description.internalOperation',
          defaultMessage: 'Ejecuta operaciones internas del sistema',
        });
        break;
      case ActionType.GeneratePlainTextFile:
        description = formatMessage({
          id: 'actions.description.generatePlainTextFile',
          defaultMessage: 'Genera archivos de texto plano',
        });
        break;
      case ActionType.ImportPlainTextFile:
        description = formatMessage({
          id: 'actions.description.importPlainTextFile',
          defaultMessage: 'Importa datos desde archivos de texto',
        });
        break;
      case ActionType.ConsumeSequence:
        description = formatMessage({
          id: 'actions.description.consumeSequence',
          defaultMessage: 'Consume secuencias de datos',
        });
        break;
      case ActionType.ExecuteRpaFlow:
        description = formatMessage({
          id: 'actions.description.executeRpaFlow',
          defaultMessage: 'Ejecuta flujos de automatización RPA',
        });
        break;
      case ActionType.Integration:
        description = formatMessage({
          id: 'actions.description.integration',
          defaultMessage: 'Realiza integraciones con sistemas externos',
        });
        break;
      case ActionType.AutoCancel:
        description = formatMessage({
          id: 'actions.description.autoCancel',
          defaultMessage: 'Cancela automáticamente el proceso',
        });
        break;
      case ActionType.AutoVoid:
        description = formatMessage({
          id: 'actions.description.autoVoid',
          defaultMessage: 'Anula automáticamente el proceso',
        });
        break;
      case ActionType.OpenAI:
        description = formatMessage({
          id: 'actions.description.openAI',
          defaultMessage: 'Integración con servicios de OpenAI',
        });
        break;
      case ActionType.ExecuteProcessAction:
        description = formatMessage({
          id: 'actions.description.executeProcessAction',
          defaultMessage: 'Ejecuta acciones de otros procesos',
        });
        break;
      case ActionType.GoogleIntegration:
        description = formatMessage({
          id: 'actions.description.googleIntegration',
          defaultMessage: 'Integración con servicios de Google',
        });
        break;
      case ActionType.RevertCancellation:
        description = formatMessage({
          id: 'actions.description.revertCancellation',
          defaultMessage: 'Revierte la cancelación del proceso',
        });
        break;
      case ActionType.RevertVoiding:
        description = formatMessage({
          id: 'actions.description.revertVoiding',
          defaultMessage: 'Revierte la anulación del proceso',
        });
        break;
      case ActionType.FTPOperation:
        description = formatMessage({
          id: 'actions.description.ftpOperation',
          defaultMessage: 'Realiza operaciones FTP de archivos',
        });
        break;
      case ActionType.RunCode:
        description = formatMessage({
          id: 'actions.description.runCode',
          defaultMessage: 'Ejecuta código personalizado',
        });
        break;
      case ActionType.AutomyAI:
        description = formatMessage({
          id: 'actions.description.automyAI',
          defaultMessage: 'Integración con Automy AI',
        });
        break;
      case ActionType.WhatsAppBusinessIntegration:
        description = formatMessage({
          id: 'actions.description.whatsAppBusinessIntegration',
          defaultMessage: 'Integración con WhatsApp Business',
        });
        break;
      default:
        description = formatMessage({
          id: 'actions.description.default',
          defaultMessage: 'Acción del proceso',
        });
    }

    actionConfigs.push({
      id,
      type,
      title,
      description,
      // Add additional properties for drag & drop compatibility
      name: title,
      displayTitle: title,
    });
  });

  // Sort by type number for consistent ordering
  return _.sortBy(actionConfigs, 'type');
}

/**
 * Gets action configuration by type
 * @param {number} type - Action type from ActionType enum
 * @param {Function} formatMessage - Intl formatMessage function
 * @returns {Object|null} Action configuration or null if not found
 */
export function getActionConfigByType(type, formatMessage) {
  const configs = generateActionConfigFromEnum(formatMessage);
  return configs.find((config) => config.type === type) || null;
}
